import { Button, ErrorText, Loader, SectionItem, Stack } from "components";
import AppointmentSummary from "./AppointmentSummary";
import PaymentSummary from "./PaymentSummary";
import VoucherPicker from "./VoucherPicker";
import {
  AppointmentType,
  Doctor,
  PatientInput,
  TimeSlot,
  Voucher,
} from "types";
import createSlotTable from "./createSlotTable";
import createPatientTable from "./createPatientTable";

type CheckoutProps = {
  type: AppointmentType;
  doctor?: Doctor;
  slot?: TimeSlot;
  patient?: PatientInput;
  voucher?: Voucher;
  error?: string;
  isCheckingOut: boolean;
  onVoucherChange(voucher: Voucher): void;
  onCheckout(): void;
};

const Checkout: React.FC<CheckoutProps> = ({
  type,
  doctor,
  slot,
  patient,
  voucher,
  error,
  isCheckingOut,
  onVoucherChange,
  onCheckout,
}) => {
  const slotTable = slot ? createSlotTable(slot) : [];
  const patientTable = patient ? createPatientTable(patient) : [];
  const discount = voucher ? voucher.discount : 0;
  const total = doctor ? Math.max(doctor.price * (1 - discount / 100), 0) : 0;

  const containerStyle: React.CSSProperties = {
    width: "calc(100% - 24px)",
    display: "grid",
    gridTemplateColumns: "50% 50%",
    gap: "24px",
  };

  const paymentWrapperStyle: React.CSSProperties = {
    position: "sticky",
    top: "-16px",
  };

  const summaryTable = [slotTable, patientTable].filter(
    (table) => table.length > 0
  );

  return (
    <div style={containerStyle}>
      <Stack gap={2} direction="column">
        <SectionItem>
          <AppointmentSummary
            type={type}
            doctor={doctor}
            table={summaryTable}
          />
        </SectionItem>

        <Stack direction="column" gap={2} style={paymentWrapperStyle}>
          <SectionItem>
            <PaymentSummary
              type={type}
              doctorId={doctor?.id}
              total={total}
              discount={{
                label: voucher ? `Voucher (${voucher.code})` : "Discount",
                percentage: discount,
              }}
            />
            {isCheckingOut && <Loader />}
          </SectionItem>

          {error && <ErrorText>{error}</ErrorText>}

          <Button
            filled
            onClick={onCheckout}
            iconEnd="arrow_forward"
            disabled={total > 0 || isCheckingOut || !doctor}
          >
            {isCheckingOut ? "Checking out" : "Checkout"}
          </Button>
        </Stack>
      </Stack>

      <VoucherPicker
        type={type}
        doctor={doctor?.id}
        active={voucher}
        disabled={isCheckingOut}
        onChange={onVoucherChange}
      />
    </div>
  );
};

export { Checkout };
